import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import { downloadZip } from './download-zip';
import {
  buildArchiveUrl,
  fetchAgentManagerFileBytes,
} from './agent-manager-api';

/**
 * Downloads one file straight to disk, byte for byte — a custom asset folder is not all
 * source code, and decoding an icon or a font to text would corrupt it.
 */
export const downloadOneAgentManagerFile = async (args: {
  solutionId: string;
  versionName: string;
  path: string;
  fileName: string;
}): Promise<void> => {
  const bytes = await fetchAgentManagerFileBytes(args);
  // `Uint8Array<ArrayBufferLike>` is wider than `BlobPart` accepts in this TS lib, though
  // the view itself is a perfectly good BlobPart at runtime.
  saveAs(
    new Blob([bytes as BlobPart], { type: 'application/octet-stream' }),
    args.fileName
  );
};

/**
 * Zips a set of files client-side.
 *
 * The archive endpoint returns the WHOLE version, but `Download all` sits inside a
 * section and must only produce that section's files — hence fetching each one. Requests
 * run in parallel and a single failure rejects, so a partial archive is never saved.
 */
export const downloadAgentManagerFilesAsZip = async (args: {
  solutionId: string;
  versionName: string;
  paths: string[];
  zipName: string;
}): Promise<void> => {
  const { solutionId, versionName, paths, zipName } = args;
  if (!paths.length) return;

  const files = await Promise.all(
    paths.map(async (path) => ({
      name: path,
      data: await fetchAgentManagerFileBytes({ solutionId, versionName, path }),
    }))
  );

  await downloadZip(zipName, files);
};

/**
 * The folder every entry in a zip sits under, `''` when they do not share one.
 *
 * The archive endpoint roots its entries at `<solutionId>/`, and a file added next to them
 * has to go under the same root or it lands outside the solution folder when unpacked.
 * Derived from the archive rather than assumed, so a change of root on the backend cannot
 * silently misplace the file.
 */
export const readArchiveRootPrefix = (entryNames: string[]): string => {
  const [first] = entryNames;
  const root = first?.includes('/') ? `${first.split('/')[0]}/` : '';
  if (!root) return '';
  return entryNames.every((name) => name.startsWith(root)) ? root : '';
};

/**
 * Downloads a whole solution version as the backend has it.
 *
 * This replaces Bot Manager's `getVersionById` + per-file `getVersionFile` for v2
 * solutions: those key off the dotted `<customer>.<bot>.<version>` id, which no solution
 * created after the migration has, so they 404 and the download fails outright.
 *
 * `extraFiles` are added into the archive — used for the grouped CSV (PSTUDIO-3443), which
 * is generated in the client and has no counterpart in the repo.
 */
export const downloadAgentManagerVersionArchive = async (args: {
  solutionId: string;
  versionName: string;
  zipName: string;
  extraFiles?: { name: string; data: string }[];
}): Promise<void> => {
  const { solutionId, versionName, zipName, extraFiles = [] } = args;

  const response = await fetch(buildArchiveUrl({ solutionId, versionName }), {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(
      `Failed to download ${versionName} (${response.status} ${response.statusText})`
    );
  }

  const archive = await response.blob();

  // Nothing to add — hand the backend's own bytes straight to the user.
  if (!extraFiles.length) {
    saveAs(archive, `${zipName}.zip`);
    return;
  }

  const zip = await JSZip.loadAsync(archive);
  const root = readArchiveRootPrefix(Object.keys(zip.files));
  extraFiles.forEach(({ name, data }) => zip.file(`${root}${name}`, data));

  saveAs(await zip.generateAsync({ type: 'blob' }), `${zipName}.zip`);
};
